
import { Component } from "vue-property-decorator";
import {
  Claim,
  ClaimCategory,
  ClaimReview,
  Factcheck,
  GoogleClaimReview,
  Partner,
  FactcheckType,
  ScrapedArticle,
  ClaimSource,
} from "@/types";

import { ROOT_ACTIONS } from "@/store/actions";

import { TipTap } from "@/mixins/TipTap";
import { mixins } from "vue-class-component";
import { Fn, useIntervalFn } from "@vueuse/core";
import * as StorageServices from "@/services/StorageServices";
import * as FactCheckService from "@/services/FactCheckService";
import * as ScraperService from "@/services/ScraperService";
import { withoutFactcheckPrefix } from "@/title";
import { describeError, ReportCmsError } from "@/services/ErrorServices";
import * as ClaimCategoryService from "@/services/ClaimCategoryService";

@Component({
  components: {
    ScrapeResult: () => import("@/components/cards/ScrapeResult.vue"),
    ConfirmationDialog: () =>
      import("@/components/dialogs/ConfirmationDialog.vue"),
    NewFactCheckCategory: () =>
      import("@/components/dialogs/NewFactCheckCategory.vue"),
    ImportClaimReviewDialog: () =>
      import("@/components/dialogs/ImportClaimReview.vue"),
    ClaimReviewForm: () => import("@/components/forms/ClaimReviewForm.vue"),
    EditFactcheckSeoDialog: () =>
      import("@/components/dialogs/EditFactcheckSeoDialog.vue"),
    ClaimForm: () => import("@/components/forms/ClaimForm.vue"),
    FactcheckForm: () => import("@/components/forms/FactcheckForm.vue"),
  },
})
export default class NewFactCheck extends mixins(TipTap) {
  step = 1;

  search = "";

  partners = [] as Partner[];

  // Track prefilled fields with metadata for displaying tooltips
  prefilledFieldsMetadata: Record<
    string,
    {
      confidence: string;
      sourceQuote: string;
      originalUrl: string;
    }
  > = {};

  today = new Date();
  date = `${this.today
    .toISOString()
    .slice(0, this.today.toISOString().indexOf("T"))}`;

  photoOverrrideWarning = false;
  imageError = false;

  imageUploadUrl = "";
  customImageSrc = "";

  imageSelectOptions = [
    { text: "Uploaden", value: "UPLOAD" },
    { text: "Afbeelding linken", value: "LINK" },
  ];

  imageSelect = "UPLOAD";

  factcheck: Partial<Factcheck> = {
    title: "",
    isPublished: true,
    description: "",
    type: FactcheckType.FACTCHECK,
    sortDate: new Date().toISOString().substring(0, 10),
    childFriendly: true,
  };

  claimReview: Partial<ClaimReview> = {
    languageCode: "",
    numericRating: 1,
    conclusion: "",
    textualRating: "",
    url: "",
    date: new Date().toISOString().substring(0, 10),
    title: "",
    PartnerId: "",
    hasVideo: false,
    isPriority: true,
    countryOfOrigin: "BE",
    imageCopyright: "",
    imageAlt: "",
  };

  claim: Partial<Claim> = {
    date: new Date().toISOString().substring(0, 10),
    claimant: "",
    claimants: [],
    source: "",
    text: "",
    associatedMedia: null,
    associatedMediaType: null,
    politicalParty: null,
    mediaformat: null,
    platform: null,
    createdAt: new Date(),
    ClaimSources: [
      {
        source: "",
        platform: null,
        mediaformat: null,
        associatedMedia: null,
        associatedMediaType: null,
        date: new Date().toISOString().substring(0, 10),
        politicalParty: null,
      }
    ]
  };

  claimSources: ClaimSource[] = [];

  claimClaimCategories = [] as ClaimCategory[];

  dialogs = {
    datePicker: false,
    articleDatePicker: false,
    createArticle: false,
    newCategory: false,
    sync: false,
    editSeo: true,
  };

  loading = {
    createArticle: false,
  };

  usedScrapedArticle = {
    id: "",
  } as ScrapedArticle;

  listClaimCategoryText() {
    const result = this.claimClaimCategories
      .map((e) => {
        if (e.id) {
          return e.nl;
        } else {
          return e;
        }
      })
      .join(",");

    return result.length === 0 ? "Geen categorie" : result;
  }

  setClaimcategories(claimCategories: ClaimCategory[]) {
    this.claimClaimCategories = claimCategories;
  }

  handleOpenSourceQuote(data: { url: string; text: string }) {
    console.log("handleOpenSourceQuote", data);
    // Create URL with text fragment for highlighting
    const urlWithHighlight = `${data.url}#:~:text=${encodeURIComponent(data.text.substring(0, 10))}`;
    console.log("Opening URL with highlight:", urlWithHighlight);
    window.open(data.url);
  }

  pauseConceptSaving: Fn | null = null;

  async mounted() {
    this.partners = await FactCheckService.GetPartners(true);

    // When used scraped article
    if (this.$route.query.article) {
      await this.setInputsFromScrapedArticle();
    } else if (this.$route.query.from === "google") {
      this.setInputsFromGoogleClaim();
    }
  }

  async fetchClaimCategories(): Promise<ClaimCategory[]> {
    try {
      const result = await ClaimCategoryService.GetClaimCategories();
      return result;
    } catch (error) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "error",
        text: "Error",
      });
      return [];
    }
  }

  createdId = null as null | string;

  async createArticle() {
    if (this.claimClaimCategories.length === 0) {
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "error",
        text: "Je moet een categorie selecteren",
      });
      return;
    }

    // The step in progress, in the editor's words: shown and logged when saving fails.
    let step = "voorbereiden";
    let claimId = "";
    try {
      this.loading.createArticle = true;
      let imageUrl = "";

      let htmlVideoInputElement = document.getElementById(
        "video"
      ) as HTMLInputElement;
      if (htmlVideoInputElement) {
        if (htmlVideoInputElement.files) {
          const video = htmlVideoInputElement.files[0];
          if (video) this.claimReview.hasVideo = true;
        }
      }

      // Handle photo
      switch (this.imageSelect) {
        case "UPLOAD": {
          step = "foto uploaden";
          const tempImageId = (Math.random() + 1).toString(36).substring(7);

          const htmlElement = document.getElementById(
            "photo"
          ) as HTMLInputElement | null;
          const file = htmlElement?.files?.[0];
          if (!file) throw new Error("geen foto gekozen");

          await StorageServices.UploadPhoto(`images/${tempImageId}`, file);
          imageUrl = await StorageServices.GetDownloadUrl(
            `images/${tempImageId}`
          );

          break;
        }

        case "SCRAPED": {
          step = "foto van het gescrapete artikel ophalen";
          imageUrl = await StorageServices.GetDownloadUrl(
            `images/${this.usedScrapedArticle.id}`
          );

          break;
        }

        case "LINK": {
          step = "foto via link";
          if (!this.customImageSrc) throw new Error("geen link naar een foto ingevuld");
          imageUrl = this.customImageSrc;
          break;
        }
      }

      // Check if new Claim Category should be created
      step = "nieuwe categorie aanmaken";
      for (let i = 0; i < this.claimClaimCategories.length; i++) {
        const cc = this.claimClaimCategories[i];
        if (typeof cc === "string") {
          const category = await ClaimCategoryService.CreateClaimCategory(cc);
          this.claimClaimCategories[this.claimClaimCategories.indexOf(cc)] =
            category;
        }
      }

      step = "claim opslaan";
      const claim = await FactCheckService.CreateClaim(this.claim);
      claimId = claim.id as string;

      step = "review en foto opslaan";
      await FactCheckService.CreateClaimReview(
        claim.id,
        {
          ...this.claimReview,
          fullText: this.$route.query.article
            ? this.usedScrapedArticle.fullArticle
            : "",
        },
        this.usedScrapedArticle.id.length > 0
          ? this.usedScrapedArticle.id
          : null,
        imageUrl
      );

      step = "factcheck opslaan";
      const factcheck = await FactCheckService.CreateFactcheck({
        ...this.factcheck,
        ClaimId: claim.id,
        sortDate: new Date(this.claimReview.date as string),
      });
      this.createdId = factcheck.id as string;

      step = "categorieën koppelen";
      await FactCheckService.SetClaimCategories(
        claim.id,
        this.claimClaimCategories.map((e) => e.id)
      );

      // Handle Video
      if (this.claimReview.hasVideo) {
        step = "video uploaden";
        //@ts-ignore
        //Video is present - ignore
        await StorageServices.UploadPhoto(
          `/videos/${factcheck.id}`,
          htmlVideoInputElement.files![0]
        );
      }

      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "success",
        text: "Artikel aangemaakt",
      });
    } catch (error) {
      console.error(error);
      const reason = describeError(error);
      const saved = this.createdId
        ? " De factcheck zelf is wel opgeslagen."
        : " De factcheck is niet opgeslagen.";
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: "error",
        text: `Opslaan mislukt bij: ${step} (${reason}).${saved}`,
        timeout: -1,
      });
      ReportCmsError({
        action: "factcheck-aanmaken",
        step,
        message: reason,
        context: {
          scrapedArticleId: (this.$route.query.article as string) || "",
          imageSelect: this.imageSelect || "",
          claimId,
          factcheckId: this.createdId || "",
          page: window.location.href,
        },
      });
    } finally {
      this.loading.createArticle = false;
      this.dialogs.createArticle = false;

      // The tick ("Gepubliceerd") in the scraper list means a factcheck exists. The API ticks it
      // once the review is saved, so a failure after that has to untick it again.
      if (this.$route.query.article) {
        try {
          await ScraperService.UpdateScrapedArticle(this.usedScrapedArticle.id, {
            used: Boolean(this.createdId),
          });
        } catch (error) {
          console.error(error);
        }
      }
      if (this.createdId) {
        this.$router.push(`/factchecks/${this.createdId}/overview?open=seo`);
      }
    }
  }

  conceptId: null | string = null;

  // Helper method to create URLs with text fragments for direct jumping to quotes
  createUrlWithTextFragment(baseUrl: string, quote: string): string {
    if (!quote || quote.trim().length === 0) {
      return baseUrl;
    }

    // Encode the quote for URL fragment and truncate if too long
    let encodedQuote = encodeURIComponent(quote.trim());

    // Text fragments work best with shorter strings, so we'll limit to first 50 chars
    if (encodedQuote.length > 50) {
      encodedQuote = encodeURIComponent(quote.trim().substring(0, 50));
    }

    return `${baseUrl}#:~:text=${encodedQuote}`;
  }

  // Helper method to check if a field was prefilled
  isFieldPrefilled(fieldName: string): boolean {
    return !!this.prefilledFieldsMetadata[fieldName];
  }

  // Helper method to get prefill info for a field
  getPrefilledInfo(fieldName: string) {
    return this.prefilledFieldsMetadata[fieldName];
  }

  // Helper method to truncate long source quotes for display
  truncateSourceQuote(quote: string, maxLength = 100): string {
    if (!quote || quote.length <= maxLength) {
      return quote;
    }
    return quote.substring(0, maxLength) + "...";
  }

  showImagePreview() {
    this.customImageSrc = URL.createObjectURL(
      //@ts-ignore
      document.getElementById("photo").files[0]
    );
    this.$forceUpdate();
  }

  async setInputsFromScrapedArticlesVersion2() {

    console.log(this.usedScrapedArticle.ScrapedArticleProperties.map(p => {
      return {
        key: p.key,
        value: p.value,
      }
    }))
    // Clear any previous prefilled metadata
    this.prefilledFieldsMetadata = {};

    if (this.usedScrapedArticle.title) {
      this.factcheck.title = withoutFactcheckPrefix(this.usedScrapedArticle.title);
    }

    if (this.usedScrapedArticle.PartnerId) {
      this.claimReview.PartnerId = this.usedScrapedArticle.PartnerId;
    }

    this.claimReview.url = this.usedScrapedArticle.url;

    const claimaints = this.usedScrapedArticle.ScrapedArticleProperties.find(
      (p) => p.key === "claimants"
    );
    if (claimaints) {
      this.claim.claimants = claimaints.value.split(",").map((c) => c.trim());
      this.prefilledFieldsMetadata["claimants"] = {
        confidence: claimaints.confidence,
        sourceQuote: claimaints.sourceQuote,
        originalUrl: this.createUrlWithTextFragment(
          this.usedScrapedArticle.url,
          claimaints.sourceQuote
        ),
      };
    }

    const introduction = this.usedScrapedArticle.ScrapedArticleProperties.find(
      (p) => p.key === "introduction"
    );

    if (introduction) {
      this.factcheck.description = introduction.value;
      this.prefilledFieldsMetadata["description"] = {
        confidence: introduction.confidence,
        sourceQuote: introduction.sourceQuote,
        originalUrl: this.createUrlWithTextFragment(
          this.usedScrapedArticle.url,
          introduction.sourceQuote
        ),
      };
    }

    const claimReviewed = this.usedScrapedArticle.ScrapedArticleProperties.find(
      (p) => p.key === "claimReviewed"
    );
    console.log("claimReviewed", claimReviewed);

    if (claimReviewed) {
      this.claim.text = claimReviewed.value;
      this.prefilledFieldsMetadata["text"] = {
        confidence: claimReviewed.confidence,
        sourceQuote: claimReviewed.sourceQuote,
        originalUrl: this.createUrlWithTextFragment(
          this.usedScrapedArticle.url,
          claimReviewed.sourceQuote
        ),
      };
    }

    const conclusion = this.usedScrapedArticle.ScrapedArticleProperties.find(
      (p) => p.key === "conclusion"
    );
    if (conclusion) {
      this.claimReview.conclusion = conclusion.value;
      this.prefilledFieldsMetadata["conclusion"] = {
        confidence: conclusion.confidence,
        sourceQuote: conclusion.sourceQuote,
        originalUrl: this.createUrlWithTextFragment(
          this.usedScrapedArticle.url,
          conclusion.sourceQuote
        ),
      };
    }

    const countryOfOrigin =
      this.usedScrapedArticle.ScrapedArticleProperties.find(
        (p) => p.key === "countryOfOrigin"
      );
    if (countryOfOrigin) {
      this.claimReview.countryOfOrigin = countryOfOrigin.value;
      this.prefilledFieldsMetadata["countryOfOrigin"] = {
        confidence: countryOfOrigin.confidence,
        sourceQuote: countryOfOrigin.sourceQuote,
        originalUrl: this.createUrlWithTextFragment(
          this.usedScrapedArticle.url,
          countryOfOrigin.sourceQuote
        ),
      };
    }

    const dateClaimMade = this.usedScrapedArticle.ScrapedArticleProperties.find(
      (p) => p.key === "dateClaimMade"
    );

    if (dateClaimMade) {
      this.claim.date = new Date(dateClaimMade.value)
        .toISOString()
        .substring(0, 10);
      this.prefilledFieldsMetadata["claimDate"] = {
        confidence: dateClaimMade.confidence,
        sourceQuote: dateClaimMade.sourceQuote,
        originalUrl: this.createUrlWithTextFragment(
          this.usedScrapedArticle.url,
          dateClaimMade.sourceQuote
        ),
      };
    }

    const datePublished = this.usedScrapedArticle.ScrapedArticleProperties.find(
      (p) => p.key === "datePublished"
    );
    if (datePublished) {
      this.claimReview.date = new Date(datePublished.value)
        .toISOString()
        .substring(0, 10);
      this.prefilledFieldsMetadata["claimReviewDate"] = {
        confidence: datePublished.confidence,
        sourceQuote: datePublished.sourceQuote,
        originalUrl: this.createUrlWithTextFragment(
          this.usedScrapedArticle.url,
          datePublished.sourceQuote
        ),
      };
    }

    const mediaFormat = this.usedScrapedArticle.ScrapedArticleProperties.find(
      (p) => p.key === "mediaFormat"
    );
    if (mediaFormat) {
      console.log("mediaFormat", mediaFormat);
      this.claim.mediaformat = mediaFormat.value;
      this.prefilledFieldsMetadata["mediaformat"] = {
        confidence: mediaFormat.confidence,
        sourceQuote: mediaFormat.sourceQuote,
        originalUrl: this.createUrlWithTextFragment(
          this.usedScrapedArticle.url,
          mediaFormat.sourceQuote
        ),
      };
    }

    const politicalParty =
      this.usedScrapedArticle.ScrapedArticleProperties.find(
        (p) => p.key === "politicalParty"
      );

    if (politicalParty) {
      this.claim.politicalParty = politicalParty.value;
      this.prefilledFieldsMetadata["politicalParty"] = {
        confidence: politicalParty.confidence,
        sourceQuote: politicalParty.sourceQuote,
        originalUrl: this.createUrlWithTextFragment(
          this.usedScrapedArticle.url,
          politicalParty.sourceQuote
        ),
      };
    }

    const platform = this.usedScrapedArticle.ScrapedArticleProperties.find(
      (p) => p.key === "platform"
    );

    if (platform) {
      this.claim.platform = platform.value;
      this.prefilledFieldsMetadata["platform"] = {
        confidence: platform.confidence,
        sourceQuote: platform.sourceQuote,
        originalUrl: this.createUrlWithTextFragment(
          this.usedScrapedArticle.url,
          platform.sourceQuote
        ),
      };
    }

    const tags = this.usedScrapedArticle.ScrapedArticleProperties.find(
      (p) => p.key === "tags"
    );
    if (tags) {
      console.log("tags", tags);
      const claimCategories = await this.fetchClaimCategories();
      const tagNames = tags.value.split(",").map((t) => t.trim().toLowerCase());

      console.log(
        claimCategories
          .filter((cc) => tagNames.includes(cc.id.toLowerCase()))
          .map((c) => {
            return {
              id: c.id,
              nl: c.nl,
            };
          })
      );
      this.claimClaimCategories = claimCategories
        .filter((cc) => tagNames.includes(cc.id.toLowerCase()))
        .map((c) => {
          return {
            id: c.id,
            nl: c.nl,
          };
        });

      this.prefilledFieldsMetadata["tags"] = {
        confidence: tags.confidence,
        sourceQuote: tags.sourceQuote,
        originalUrl: this.createUrlWithTextFragment(
          this.usedScrapedArticle.url,
          tags.sourceQuote
        ),
      };
    }

    const textualRating = this.usedScrapedArticle.ScrapedArticleProperties.find(
      (p) => p.key === "textualRating"
    );

    if (textualRating) {
      this.claimReview.textualRating = textualRating.value;
      this.prefilledFieldsMetadata["textualRating"] = {
        confidence: textualRating.confidence,
        sourceQuote: textualRating.sourceQuote,
        originalUrl: this.createUrlWithTextFragment(
          this.usedScrapedArticle.url,
          textualRating.sourceQuote
        ),
      };
    }

    const source = this.usedScrapedArticle.ScrapedArticleProperties.find(
      (p) => p.key === "source"
    );

    if (source) {
      this.claim.source = source.value;
      this.claim.ClaimSources = [
        {
          source: source.value,
          platform: this.claim.platform ?? null,
          mediaformat: this.claim.mediaformat ?? null,
          associatedMedia: null,
          associatedMediaType: null,
          date: this.claim.date?.toString() ?? null,
          politicalParty: this.claim.politicalParty ?? null,
        }
      ];
      this.prefilledFieldsMetadata["source"] = {
        confidence: source.confidence,
        sourceQuote: source.sourceQuote,
        originalUrl: this.createUrlWithTextFragment(
          this.usedScrapedArticle.url,
          source.sourceQuote
        ),
      };
    }
  }

  async setInputsFromScrapedArticle() {
    this.imageSelectOptions.unshift({
      text: "Gescrapte afbeelding",
      value: "SCRAPED",
    });
    this.imageSelect = "SCRAPED";

    this.usedScrapedArticle = await ScraperService.GetScrapedArticle(
      this.$route.query.article as string
    );

    if (this.usedScrapedArticle.version === 2) {
      await this.setInputsFromScrapedArticlesVersion2();
      return;
    }

    if (
      ["DPA", "NIEUWSCHECKERS_NL", "AFP"].includes(
        this.usedScrapedArticle.PartnerId
      )
    ) {
      this.claimReview.countryOfOrigin = "NL";
    } else {
      this.claimReview.countryOfOrigin = "BE";
    }

    this.usedScrapedArticle.id = this.$route.query.article as string;
    this.factcheck.title = withoutFactcheckPrefix(this.usedScrapedArticle.title);
    this.factcheck.description = this.usedScrapedArticle.description;

    this.claimReview.PartnerId = this.usedScrapedArticle.PartnerId;

    this.claimReview.title = this.usedScrapedArticle.title;
    this.claimReview.url = this.usedScrapedArticle.url;
    this.claimReview.date = this.usedScrapedArticle.date
      ? new Date(this.usedScrapedArticle.date).toISOString().substring(0, 10)
      : new Date(this.usedScrapedArticle.createdAt)
        .toISOString()
        .substring(0, 10);

    this.claimReview.publisher = this.usedScrapedArticle.factCheckAuthor;
    this.claimReview.url = this.usedScrapedArticle.url;

    if (this.usedScrapedArticle.claimReviewData) {
      console.log("ClaimReviewData", this.usedScrapedArticle.claimReviewData);
      let cr =
        typeof this.usedScrapedArticle.claimReviewData === "string"
          ? JSON.parse(this.usedScrapedArticle.claimReviewData)
          : this.usedScrapedArticle.claimReviewData;

      if (this.usedScrapedArticle.PartnerId === "AFP") {
        cr = cr["@graph"][0];
      }

      if (cr.claimReviewed) {
        this.claim.text = cr.claimReviewed;
      }

      if (cr.itemReviewed.author) {
        this.claim.claimant = cr.itemReviewed.author.name;
      }

      if (cr.itemReviewed.appearance[0]) {
        this.claim.source = cr.itemReviewed.appearance[0].url || "";
        this.claim.ClaimSources = [
          {
            source: cr.itemReviewed.appearance[0].url || "",
            platform: null,
            mediaformat: null,
            associatedMedia: null,
            associatedMediaType: null,
            date: null,
            politicalParty: null,
          }
        ];
      }

      if (
        cr.itemReviewed &&
        cr.itemReviewed.author &&
        cr.itemReviewed.author.name
      ) {
        this.claim.claimants = [cr.itemReviewed.author.name];
      }

      if (cr.reviewRating.alternateName) {
        if (cr.author.name === "dpa") {
          this.claimReview.conclusion = cr.reviewRating.alternateName;
        }
        this.claimReview.textualRating = cr.reviewRating.alternateName;
      }

      if (cr.reviewRating.ratingValue) {
        this.claimReview.numericRating = parseInt(
          cr.reviewRating.ratingValue,
          10
        );
      }

      if (cr.datePublished) {
        this.claimReview.date = new Date(cr.datePublished)
          .toISOString()
          .substring(0, 10);
      }

      if (cr.itemReviewed.datePublished) {
        this.claim.date = new Date(cr.itemReviewed.datePublished)
          .toISOString()
          .substring(0, 10);
      }
    }
  }

  async setInputsFromConcept() {
    console.log("Set inputs from concept");
    // this.conceptId = this.$route.query.concept as string;
    // const concept = await FactCheckService.GetConcept(this.conceptId);
    // this.factcheck = concept.factcheck;
    // this.claim = concept.claim;
    // this.claimReview = concept.claimReview;
  }

  setInputsFromGoogleClaim() {
    const claim = JSON.parse(
      this.$route.query.claim as string
    ) as GoogleClaimReview;
    this.claim.text = claim.text;

    this.claimReview.PartnerId = "UNKNOWN";
    this.claimReview.textualRating = claim.claimReview[0].textualRating;
    this.claimReview.url = claim.claimReview[0].url;
    this.factcheck.title = withoutFactcheckPrefix(claim.claimReview[0].title);
  }
}
